import faker from '@faker-js/faker'
import {AreaNode, UpmEntityType} from '@hconnect/common/types'
import MockAdapter from 'axios-mock-adapter'

import {
  numberRegEx,
  sleepResponse,
  uuidRegEx,
  saveScenario
} from '@settings/__mock__/mockServices/utils'
import {mockStore} from '@settings/__mock__/mockStore'
import {
  AreaCreateType,
  DepartmentCreateType
} from '@settings/modules/plant-setup/types/departmentConfig'
import {Equipment} from '@settings/modules/upm/types/equipment'
import {SignalDetail, SignalListData} from '@settings/modules/upm/types/signal'

export const enableUpmEndpoints = (mock: MockAdapter) => {
  // ----- UPM structure endpoints -----

  // GET upm plant restore structure
  mock.onGet(new RegExp(`upm/plants/${numberRegEx}/structure/removed`)).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.restoreStructure])
  })

  // GET upm plant structure
  mock.onGet(new RegExp(`upm/plants/${numberRegEx}/structure`)).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.structure])
  })

  // ----- UPM general tab endpoints -----

  // GET plant general data
  mock.onGet(new RegExp(`/upm/plant/${uuidRegEx}`)).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {
        upm: {plantData}
      }
    } = scenario
    return sleepResponse([200, plantData])
  })

  // GET list of upm plants
  mock.onGet(new RegExp('/upm/plant')).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {
        upm: {plants}
      }
    } = scenario
    return sleepResponse([200, plants])
  })

  // ----- UPM sensors tab endpoints -----

  // GET list of signals for plant/area/production-line/department/equipment
  const nodeType = '(plant|area|production-line|department)'
  mock.onGet(new RegExp(`/upm/signal/${nodeType}/${uuidRegEx}\\?pageSize=1000`)).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.signals])
  })

  // GET signal details
  mock.onGet(new RegExp(`/upm/signal/${uuidRegEx}`)).reply((config) => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario

    const signalId = (config.url as string).split('/')[3]
    const signal = upm.signals.find((signal) => signal.id === signalId)

    return sleepResponse([200, signal])
  })

  // GET signal types (used in signal details)
  mock.onGet('upm/signal-type').reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.signalType])
  })

  // GET units (used in signal details)
  mock.onGet('upm/unit').reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.units])
  })

  // GET equipments (used in signal details)
  mock
    .onGet(new RegExp(`upm/equipment/plant/${numberRegEx}\\?pageSize=${numberRegEx}`))
    .reply(() => {
      const scenario = mockStore.scenario()
      const {
        burglengenfeld: {upm}
      } = scenario
      return sleepResponse([200, upm.equipment])
    })

  // GET local names (used in signal details)
  mock.onGet(new RegExp(`upm/signal/plant/${numberRegEx}/local-names`)).reply(() => {
    return sleepResponse([200, []])
  })

  // GET area details
  mock.onGet(new RegExp(`upm/area/${numberRegEx}`)).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.area])
  })

  // GET department details
  mock.onGet(new RegExp(`upm/department/${uuidRegEx}`)).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.department])
  })

  // GET department types for area
  mock.onGet(new RegExp('upm/department-type/')).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.departmentTypes])
  })

  // GET area types
  mock.onGet(new RegExp('upm/area-type')).reply(() => {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {upm}
    } = scenario
    return sleepResponse([200, upm.areaTypes])
  })

  // POST create area
  mock.onPost('upm/area').reply((config) => {
    const scenario = mockStore.scenario()
    const payload = JSON.parse(config.data as string) as AreaCreateType
    const newArea: AreaNode = {
      upmId: faker.datatype.uuid(),
      text: payload.typeText,
      children: [],
      itemType: UpmEntityType.Area,
      orderGroup: 0
    }
    scenario.burglengenfeld.upm.structure.children = [
      ...scenario.burglengenfeld.upm.structure.children,
      newArea
    ]

    saveScenario(scenario)

    return sleepResponse([201, newArea])
  })

  // POST create signal
  mock.onPost('/upm/signal').reply((config) => {
    const scenario = mockStore.scenario()
    const payload = JSON.parse(config.data as string) as SignalDetail
    const newSignal: SignalListData = {
      ...payload,
      id: faker.datatype.uuid(),
      area: {
        id: faker.datatype.uuid(),
        name: 'Area'
      },
      equipment: {
        id: payload.equipmentId,
        name: 'Equipment'
      },
      department: {
        id: faker.datatype.uuid(),
        name: 'Department'
      },
      productionLine: {
        id: faker.datatype.uuid(),
        name: 'Production Line'
      }
    }
    scenario.burglengenfeld.upm.signals = [...scenario.burglengenfeld.upm.signals, newSignal]

    saveScenario(scenario)

    return sleepResponse([201, newSignal])
  })

  // POST create department
  mock.onPost('/upm/department').reply((config) => {
    const scenario = mockStore.scenario()
    const payload = JSON.parse(config.data as string) as DepartmentCreateType
    const newDepartment: AreaNode = {
      upmId: faker.datatype.uuid(),
      text: payload.displayValue,
      children: [],
      itemType: UpmEntityType.Area,
      orderGroup: 0
    }
    scenario.burglengenfeld.upm.structure.children = [
      ...scenario.burglengenfeld.upm.structure.children,
      newDepartment
    ]

    saveScenario(scenario)

    return sleepResponse([201, newDepartment])
  })

  // PUT update department
  mock.onPut(new RegExp(`upm/department/${uuidRegEx}`)).reply((config) => {
    const scenario = mockStore.scenario()
    const payload = JSON.parse(config.data as string) as Equipment
    const updatedDepartment: Equipment = {
      ...payload,
      upmId: payload.upmId,
      displayValueEnglish: payload.displayValueEnglish ?? ''
    }
    scenario.burglengenfeld.upm.department = updatedDepartment

    saveScenario(scenario)

    return sleepResponse([200, updatedDepartment])
  })

  // DELETE delete department
  mock.onDelete(`/upm/department/${uuidRegEx}`).reply(() => {
    const scenario = mockStore.scenario()

    scenario.burglengenfeld.upm.structure.children.pop()

    saveScenario(scenario)

    return sleepResponse([200, {}])
  })

  // DELETE delete equipment
  mock.onDelete(new RegExp(`/upm/equipment/${uuidRegEx}`)).reply(() => {
    const scenario = mockStore.scenario()

    scenario.burglengenfeld.upm.structure.children.pop()

    saveScenario(scenario)

    return sleepResponse([200, {}])
  })

  // PATCH restore department
  mock.onPatch(new RegExp(`/upm/department/${uuidRegEx}/undelete`)).reply(() => {
    const scenario = mockStore.scenario()

    scenario.burglengenfeld.upm.restoreStructure.children[1].children[0].children.pop()

    saveScenario(scenario)

    return sleepResponse([200, {}])
  })

  // PATCH restore equipment
  mock.onPatch(new RegExp(`/upm/equipment/${uuidRegEx}/undelete`)).reply(() => {
    const scenario = mockStore.scenario()

    scenario.burglengenfeld.upm.restoreStructure.children[0].children[0].children[0].children.pop()

    saveScenario(scenario)

    return sleepResponse([200, {}])
  })

  return mock
}
